import { useState } from 'react';
import { Modal } from './Shared';
import { attrsFor, GK_ATTR, POSITIONS, POSITION_LABELS, DEFAULT_ATTRS } from '../lib/constants';
import { computeBaseOverall } from '../lib/balance';

export default function PlayerFormModal({ player, allowNameEdit, onSave, onCancel, saving, error }) {
  const [name, setName] = useState(player.display_name || player.name || '');
  const [positions, setPositions] = useState(player.positions || []);
  const [attributes, setAttributes] = useState({ ...DEFAULT_ATTRS, ...(player.attributes || {}) });

  const togglePosition = (pos) =>
    setPositions((prev) => (prev.includes(pos) ? prev.filter((p) => p !== pos) : [...prev, pos]));

  // Reacts as they tick GK, so the slider appears the moment it's relevant.
  const shown = attrsFor(positions);
  const overall = computeBaseOverall(attributes);
  const nameMissing = allowNameEdit && !name.trim();

  const submit = (e) => {
    e.preventDefault();
    if (nameMissing) return;
    // Drop a stale goalkeeping value if they no longer list GK — left behind it
    // would keep sitting in the record for a position they don't play.
    const kept = { ...attributes };
    if (!positions.includes('GK')) delete kept[GK_ATTR.key];
    onSave({ ...player, display_name: name.trim(), positions, attributes: kept });
  };

  return (
    <Modal
      title={player.id ? 'Edit player' : 'Add guest player'}
      subtitle="Positions are ranked — the first one you pick is treated as their strongest."
      onClose={onCancel}
      wide
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" form="player-form" className="btn btn--primary" disabled={saving || nameMissing}>
            {saving ? 'Saving…' : 'Save player'}
          </button>
        </>
      }
    >
      <form id="player-form" onSubmit={submit} className="stack">
        {error && <p className="form-error">{error}</p>}

        {allowNameEdit && (
          <label className="field">
            <span className="field__label">Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Danny"
              autoFocus
            />
          </label>
        )}

        <div className="field">
          <span className="field__label">
            Positions {positions.length > 0 && <em className="field__hint">ranked in the order you tap them</em>}
          </span>
          <div className="pos-grid">
            {POSITIONS.map((pos) => {
              const rank = positions.indexOf(pos);
              return (
                <button
                  type="button"
                  key={pos}
                  className={`pos-chip ${rank > -1 ? 'pos-chip--on' : ''}`}
                  onClick={() => togglePosition(pos)}
                  title={POSITION_LABELS[pos]}
                >
                  {pos}
                  {rank > -1 && <span className="pos-chip__rank">{rank + 1}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="field">
          <span className="field__label">
            Attributes <em className="field__hint">overall {overall.toFixed(1)}</em>
          </span>
          <div className="attrs">
            {shown.map((a) => (
              <label key={a.key} className="attr">
                <span className="attr__label">{a.label}</span>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={attributes[a.key] ?? 5}
                  onChange={(e) => setAttributes((prev) => ({ ...prev, [a.key]: Number(e.target.value) }))}
                  className="attr__slider"
                />
                <span className="attr__value">{attributes[a.key] ?? 5}</span>
              </label>
            ))}
          </div>
          <p className="field__note">
            Attributes are your judgement of a player's ability and are never changed automatically. Form —
            the small bonus or penalty from recent games — is layered on top and decays week by week.
          </p>
          {positions.includes('GK') && (
            <p className="field__note">
              Goalkeeping only appears for players who list GK, and doesn't count towards the overall
              above — it decides who goes in goal, nothing else. Everyone is rated on the same seven
              attributes so the numbers stay comparable.
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
}
